/** @file clip.create: adds an empty clip with the format defaults. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { refuseAttachmentPrefix } from '../validate/refuseAttachmentPrefix';

/** `clip.create { name, frames, fps?, loop?, category? }` → `{ id, name }`; defaults fps 30, loop, `misc`; inverse: delete. */
export const clipCreateCommand: CommandDef<'clip.create'> = {
  name: 'clip.create',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['name', 'frames', 'fps', 'loop', 'category']);
    return compact({
      name: r.str('name'), frames: r.int('frames', { min: 1 }), fps: r.opt('fps', (k) => r.num(k, { above: 0 })),
      loop: r.opt('loop', r.bool), category: r.opt('category', r.str),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    refuseAttachmentPrefix(input.name, '/input/name');
    if (Object.hasOwn(draft.clips, input.name)) {
      throwError(ERROR_CODES.VALIDATION, { clip: input.name }, { path: '/input/name', hint: 'a clip with this name exists; clip.rename or clip.delete it' });
    }
    const id = ctx.makeId('clp');
    draft.clips[input.name] = {
      format: 'pose3d/clip@1', id, name: input.name, fps: input.fps ?? 30, frames: input.frames, loopStart: 0, start: 0,
      loop: input.loop ?? true, mirrorHalf: false, category: input.category ?? 'misc', keys: [],
    };
    return { result: { id, name: input.name }, warnings: [], inverse: [{ name: 'clip.delete', input: { clip: input.name } }] };
  },
};
